'use client'

import { useTranslations } from 'next-intl'

import { SegmentedControl } from '@/components/ui/segmented-control'
import { FormSection } from '@/features/order/order-form/components/widget/order-form-fields'
import { TwapIntervalInput } from '@/features/order/order-form/components/widget/twap-interval-input'
import { SingleHedgeExecution } from '@/types/generated/algo'

// LIMIT is not offered: the single hedge refuses it.
const EXECUTION_OPTIONS = [
  { value: SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_UNSPECIFIED, labelKey: 'executionAuto' },
  { value: SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_MARKET, labelKey: 'executionMarket' },
  { value: SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_TWAP, labelKey: 'executionTwap' },
  {
    value: SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_LIMIT_CHASE,
    labelKey: 'executionLimitChase',
  },
] as const

interface SingleHedgeExecutionFieldsProps {
  execution: SingleHedgeExecution
  twapSeconds: string
  errors: Record<string, string>
  onChange: (patch: Partial<{ execution: SingleHedgeExecution; twapSeconds: string }>) => void
}

/**
 * How a single hedge's order is worked: the execution style and, for TWAP, its duration.
 * Four fixed modes, so a segmented control rather than a dropdown — every option is visible
 * at once, like the order form's other mode switches. The TWAP duration reuses the TWAP tab's
 * own D/H/M/S picker (it already speaks seconds) so the two read as the same field.
 */
export function SingleHedgeExecutionFields({
  execution,
  twapSeconds,
  errors,
  onChange,
}: SingleHedgeExecutionFieldsProps) {
  const t = useTranslations('trade.hedgeBuilder')
  const tf = useTranslations('orderForm')
  const isTwap = execution === SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_TWAP

  return (
    <div className="flex flex-col gap-4">
      <FormSection label={t('execution')} info={t('singleHedgeExecutionHelp')}>
        <SegmentedControl<string>
          fill
          options={EXECUTION_OPTIONS.map((o) => ({ value: String(o.value), label: t(o.labelKey) }))}
          value={String(execution)}
          onChange={(v) => onChange({ execution: Number(v) as SingleHedgeExecution })}
        />
      </FormSection>

      {isTwap && (
        <FormSection
          label={tf('duration')}
          error={errors.twapSeconds}
          hint={twapSeconds ? undefined : t('twapDurationPlaceholder')}
        >
          <TwapIntervalInput value={twapSeconds} onChange={(v) => onChange({ twapSeconds: v })} />
        </FormSection>
      )}
    </div>
  )
}
